import { clientConfig, notify, text } from "@quickpoint.me/shared";
import { getAuth, getIdToken } from "firebase/auth";
import { useState } from "react";
import { useRecoilCallback, useRecoilValue } from "recoil";
import { Authorization } from "../state/authAtoms";
import { StackCfKeyAtom } from "../state/stackAtoms";
import { ComposedStack } from "../state/stackSelectors";
import { DemoMode } from "../state/uiAtoms";

export type PdfStatus = "idle" | "loading";

/**
 * PDF export of the current story. Deliberately does NOT require the story
 * to be published — the export renders from the saved stack, so it is
 * available anywhere it is offered (preview bar, share panel).
 */
export function usePdfExport() {
	const stackCfKey = useRecoilValue(StackCfKeyAtom);
	const isLoggedIn = useRecoilValue(Authorization).isLoggedIn;
	const demoMode = useRecoilValue(DemoMode);
	const [pdfStatus, setPdfStatus] = useState<PdfStatus>("idle");

	const canExportPdf =
		(isLoggedIn || demoMode) && !!stackCfKey && stackCfKey !== "";

	// useRecoilCallback so ComposedStack (which reads async selectors that can
	// suspend) is only read at click time from a snapshot — reading it during
	// render suspended the button synchronously (React error #426).
	const handleExportPdf = useRecoilCallback(
		({ snapshot }) =>
			async () => {
				if (!canExportPdf || pdfStatus === "loading") return;

				setPdfStatus("loading");
				try {
					const parts = stackCfKey.split("/");
					if (parts.length < 2) throw new Error("Invalid stack key");
					const stack = parts.slice(1).join("/");

					// Signed-in authors POST their CURRENT stack to the JWT-protected
					// endpoint, so export works regardless of publish state (drafts, EU
					// decks). The public demo has no JWT, so it falls back to the
					// published-KV GET path.
					let response: Response;
					if (isLoggedIn) {
						const user = getAuth().currentUser;
						const jwt = user ? await getIdToken(user) : null;
						if (!jwt) throw new Error("Not signed in");
						const composedStack = await snapshot.getPromise(ComposedStack);
						response = await fetch(clientConfig.pdfGenerate(), {
							method: "POST",
							headers: {
								"Content-Type": "application/json",
								Authorization: `Bearer ${jwt}`,
							},
							body: JSON.stringify(composedStack),
						});
					} else {
						response = await fetch(clientConfig.pdfExport(parts[0], stack));
					}

					if (!response.ok) {
						const json = await response.json().catch(() => null);
						const detail =
							json?.message || json?.error || `status ${response.status}`;
						throw new Error(detail);
					}

					const blob = await response.blob();
					const blobUrl = window.URL.createObjectURL(blob);
					const a = document.createElement("a");
					a.href = blobUrl;
					a.download = `${stack.replace(/\//g, "-")}.pdf`;
					document.body.appendChild(a);
					a.click();
					window.URL.revokeObjectURL(blobUrl);
					document.body.removeChild(a);
					setPdfStatus("idle");
				} catch (error) {
					console.error("PDF export error:", error);
					setPdfStatus("idle");
					const detail = error instanceof Error ? error.message : "";
					notify(`${text.preview.exportPdfError} ${detail}`.trim());
				}
			},
		[canExportPdf, pdfStatus, stackCfKey, isLoggedIn],
	);

	return { pdfStatus, canExportPdf, handleExportPdf };
}
